import { useState, useCallback, useEffect, useRef } from 'react';
import { ChatMessage } from '../domain/PatientChat';
import { PatientChatApiService } from '../infrastructure/api/PatientChatApiService';
import { PatientChatHistoryApiService } from '../infrastructure/api/PatientChatHistoryApiService';

interface UsePatientChatOptions {
  persist?: boolean;
}

export const usePatientChat = (patientId: string, options: UsePatientChatOptions = {}) => {
  const { persist = false } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Stable ref so sendMessage can read the current history without being a dep
  const messagesRef = useRef<ChatMessage[]>(messages);
  messagesRef.current = messages;

  // Load history on mount when persist=true
  useEffect(() => {
    setMessages([]);
    setError(null);

    if (persist && patientId) {
      PatientChatHistoryApiService.load(patientId)
        .then((loaded) => setMessages(loaded))
        .catch(() => {/* silently ignore */});
    }
  }, [patientId, persist]);

  // Abort any in-flight request on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim()) return;

      // Read current history via ref — avoids adding `messages` to deps,
      // which would recreate this callback on every message and cause stale closures.
      const currentHistory = messagesRef.current;
      const userMessage: ChatMessage = { role: 'user', content: text };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await PatientChatApiService.sendMessage(patientId, {
          message: text,
          history: currentHistory,
        });
        const assistantMessage: ChatMessage = {
          role: 'model',
          content: response.data.response,
        };
        const updatedMessages = [...currentHistory, userMessage, assistantMessage];
        setMessages(updatedMessages);

        if (persist) {
          PatientChatHistoryApiService.save(patientId, updatedMessages).catch(() => {/* silently ignore */});
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'CanceledError') return;
        setError('chat.error');
      } finally {
        setIsLoading(false);
        abortRef.current = null;
      }
    },
    // `messages` removed — read via messagesRef.current for a stable callback
    [patientId, persist],
  );

  return { messages, isLoading, error, sendMessage };
};
